import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  SimpleChanges,
  untracked,
  ViewChild,
} from '@angular/core'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'
import { ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatDialog, MatDialogModule } from '@angular/material/dialog'
import { MatSnackBarModule } from '@angular/material/snack-bar'
import { Router } from '@angular/router'
import {
  ApiService,
  AppointmentModel,
  ChargeFormService,
  ChargeGrouping,
  Clinic,
  ConfirmService,
  DirectivesModule,
  Estimate,
  EstimateBalance,
  EstimateBalanceWorkflowStatus,
  EstimateChargeDiscountType,
  EstimateData,
  EstimateFormGroup,
  Facility,
  IsDirty,
  ModelClaimSearchResult,
  Patient,
  PatientDataService,
  PatientInsurance,
  PermissionsService,
  TransactionModel,
} from '@mmx/shared'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { combineLatest, of, Subject } from 'rxjs'
import { catchError, concatMap, filter, map, startWith, take, takeUntil, tap } from 'rxjs/operators'

import { BreadcrumbModule } from '../breadcrumb'
import { InsurancesCardModule } from '../insurances-card/insurances-card.module'
import { openJsonTraceDialog, openTraceMessageDialog } from '../json-trace-dialog'
import { buildPricerTraceMessage, findPricerTraceEntry, openPricerTraceDialog } from '../pricer-trace-message'
import { SendContactEditDialogModule } from '../send-contact-edit-dialog'
import { BalanceSendChannel } from './balance-summary.types'
import { isConfirmedWorkflow } from './balance-utils'
import { BalanceBenefitsPanelComponent } from './components/balance-benefits-panel/balance-benefits-panel.component'
import { BalanceChargesPanelComponent } from './components/balance-charges-panel/balance-charges-panel.component'
import { BalanceChargesResetType } from './components/balance-charges-toolbar/balance-charges-toolbar.component'
import { BalanceCharityCareSectionComponent } from './components/balance-charity-care-section/balance-charity-care-section.component'
import { BalanceCollectPaymentCardComponent } from './components/balance-collect-payment-card/balance-collect-payment-card.component'
import {
  BalanceHistoryDialogComponent,
  BalanceHistoryDialogData,
} from './components/balance-history-dialog/balance-history-dialog.component'
import {
  BalanceRefundDialogComponent,
  BalanceRefundDialogData,
  BalanceRefundDialogResult,
} from './components/balance-refund-dialog/balance-refund-dialog.component'
import { BalanceTransactionsCardComponent } from './components/balance-transactions-card/balance-transactions-card.component'
import { EstimateModifySessionComponent } from './modify-session/estimate-modify-session.component'
import {
  BalanceChargeBreakdownDialogComponent,
  BalanceChargeBreakdownDialogData,
} from './review-multi-visit/components/balance-charge-breakdown-dialog/balance-charge-breakdown-dialog.component'
import { EstimateReviewMultiVisitComponent } from './review-multi-visit/estimate-review-multi-visit.component'
import { BalanceActionRunner } from './services/balance-action-runner.service'
import { BalanceSnackbarController } from './services/balance-snackbar-controller.service'
import { ClaimSearchController } from './services/claim-search-controller.service'
import { EstimateBalanceApiService } from './services/estimate-balance-api.service'
import { EstimateBalanceFlowService } from './services/estimate-balance-flow.service'
import { BenefitsFormGroup, EstimateBalanceFormService } from './services/estimate-balance-form.service'
import {
  EstimateBalancePresenterService,
  EstimateBalanceViewModel,
} from './services/estimate-balance-presenter.service'
import { EstimateBalanceStateService } from './services/estimate-balance-state.service'
import { EstimateInsuranceLoader } from './services/estimate-insurance-loader.service'
import { EstimateTemplateLoaderService } from './services/estimate-template-loader.service'

@Component({
  selector: 'mmx-estimate-balance',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatSnackBarModule,
    TranslateModule,
    BreadcrumbModule,
    InsurancesCardModule,
    BalanceChargesPanelComponent,
    BalanceBenefitsPanelComponent,
    BalanceCharityCareSectionComponent,
    BalanceTransactionsCardComponent,
    EstimateReviewMultiVisitComponent,
    EstimateModifySessionComponent,
    BalanceCollectPaymentCardComponent,
    SendContactEditDialogModule,
    DirectivesModule,
  ],
  providers: [
    EstimateBalanceStateService,
    EstimateBalancePresenterService,
    EstimateBalanceFormService,
    EstimateTemplateLoaderService,
    EstimateBalanceFlowService,
    BalanceSnackbarController,
    ClaimSearchController,
    ChargeFormService,
    EstimateInsuranceLoader,
    BalanceActionRunner,
  ],
  templateUrl: './estimate-balance.component.html',
})
export class EstimateBalanceComponent implements OnInit, OnChanges, OnDestroy, IsDirty {
  @Input() estimateId: string | null = null
  @Input() patient: Patient | null = null
  @Input() clinic: Clinic | null = null
  @Input() facility: Facility | null = null
  @Input() appointment: AppointmentModel | null = null
  // Routerless host (embedded web component): suppress navigation, signal via outputs instead.
  @Input() embedded = false

  @Output() estimateSaved = new EventEmitter<EstimateData>()
  @Output() estimateDeleted = new EventEmitter<void>()

  // Runner's signal so OnPush re-renders without manual CD. Read-only to callers.
  get busy(): boolean {
    return this.actions.busy()
  }

  readonly estimateForm: EstimateFormGroup
  readonly benefitsForm: BenefitsFormGroup
  vm!: Signal<EstimateBalanceViewModel>

  private readonly clinicSig = signal<Clinic | null>(null)
  private readonly facilitySig = signal<Facility | null>(null)
  private readonly patientSig = signal<Patient | null>(null)
  private readonly localMcsTrace = signal<ModelClaimSearchResult['trace'] | null>(null)
  private readonly isSystemAdministrator = toSignal(this.permissionsService.isSystemAdministrator$, {
    injector: this.injector,
    initialValue: this.permissionsService.isSystemAdministrator(),
  })
  readonly showMcsResponseButton = computed(
    () => this.isSystemAdministrator() && (this.vm?.().hasEstimateLevelMcsTrace ?? false),
  )
  private readonly estimateInputs = computed(() => this.state.estimate()?.inputs ?? null)
  private readonly loadedEstimateId = computed(() => this.state.estimate()?.id ?? null)
  private readonly destroy$ = new Subject<void>()

  @ViewChild(BalanceTransactionsCardComponent) transactionsCard?: BalanceTransactionsCardComponent
  // Plan panel is embedded in the collect-payment card; refresh delegates through it.
  @ViewChild(BalanceCollectPaymentCardComponent) collectPaymentCard?: BalanceCollectPaymentCardComponent

  constructor(
    private readonly state: EstimateBalanceStateService,
    private readonly presenter: EstimateBalancePresenterService,
    private readonly formService: EstimateBalanceFormService,
    private readonly flow: EstimateBalanceFlowService,
    private readonly api: EstimateBalanceApiService,
    private readonly router: Router,
    private readonly injector: Injector,
    private readonly confirmService: ConfirmService,
    private readonly dialog: MatDialog,
    private readonly snackbar: BalanceSnackbarController,
    private readonly claimSearch: ClaimSearchController,
    private readonly insuranceLoader: EstimateInsuranceLoader,
    private readonly actions: BalanceActionRunner,
    private readonly translate: TranslateService,
    private readonly permissionsService: PermissionsService,
    public readonly patientData: PatientDataService,
  ) {
    this.estimateForm = this.formService.buildEstimateForm()
    this.benefitsForm = this.formService.buildBenefitsForm()
    this.flow.attachForm(this.estimateForm)
    this.flow.attachBenefitsForm(this.benefitsForm)
  }

  ngOnInit(): void {
    // claimSearch is genuinely RxJS (form valueChanges + permissions) — bridge to signals
    // here. Explicit injector because toSignal runs in ngOnInit (post first ngOnChanges).
    this.vm = this.presenter.viewModel({
      clinic: this.clinicSig,
      facility: this.facilitySig,
      patient: this.patientSig,
      charityCareStatus: toSignal(this.patientData.charityCareStatus$, {
        injector: this.injector,
        initialValue: undefined,
      }),
      claimSearchEnabled: toSignal(
        this.claimSearch.enabled$(this.estimateForm, () => this.clinic),
        { injector: this.injector, initialValue: false },
      ),
      claimSearchPrefillQuery: toSignal(
        this.claimSearch.prefillQuery$(this.estimateForm),
        { injector: this.injector, initialValue: null },
      ),
      hasLocalMcsTrace: computed(() => this.localMcsTrace() != null),
    })

    // Fires only on actual inputs-ref swaps (computed dedupes by ref), not every dirty/visit tick.
    effect(() => {
      const inputs = this.estimateInputs()
      if (inputs) {
        this.formService.patchBenefitsForm(this.benefitsForm, inputs)
      }
    }, { injector: this.injector })

    // Seed CoC + unscheduled flags from the loaded estimate. Keyed on id so it re-runs only
    // on estimate swaps; untracked() reads count/mode without widening deps so manual
    // toggles (setCocMode) stick across unrelated state changes.
    effect(() => {
      this.loadedEstimateId()
      const estimate = untracked(() => this.state.estimate())
      const unscheduled = estimate?.mode === Estimate.MODE.UNSCHEDULED
      const count = estimate?.appointmentIds?.length ?? 0
      this.state.setCocMode(count > 1 || unscheduled)
      this.state.setUnscheduled(unscheduled)
    }, { injector: this.injector })

    this.insuranceLoader.attach()
    // Reprice prompt is for already-calculated estimates only: a draft with no balance offers
    // just the plain Calculate button. Form-invalid is gated too — the action would 400/500.
    const snackbarVisible$ = combineLatest([
      toObservable(this.state.needsPricing, { injector: this.injector }),
      toObservable(this.state.balance, { injector: this.injector }),
      this.estimateForm.statusChanges.pipe(startWith(this.estimateForm.status)),
    ]).pipe(
      map(([needsPricing, balance, status]) => needsPricing && balance != null && status === 'VALID'),
    )
    // onCalculate, not save() — save() returns the estimate only and leaves state.balance
    // (patient responsibility) stale; saveAndCalculate (via onCalculate when dirty) refreshes both.
    this.snackbar.attach(snackbarVisible$, () => this.onCalculate())
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['estimateId']) {
      // Skip refetch when the parent re-pushes the same id we just hydrated (post-save 'new' → :id swap).
      if (!this.estimateId || this.estimateId !== this.state.estimate()?.id) {
        this.hydrate()
      }
    } else if (changes['appointment'] && this.isNewEstimate && this.state.estimate()?.id == null) {
      // Unscheduled CoC estimates have no appointmentIds, so the backend never links
      // appointment.estimateId back to them. An embedded host that refetches its appointment
      // after estimateSaved (see mmx-main) swaps this input while [estimateId] (bound to
      // appointmentModel?.estimateId) stays null — without the state check, isNewEstimate alone
      // would re-hydrate a blank draft over the estimate/balance a just-run Calculate produced.
      this.hydrate()
    }
    if (changes['clinic']) {
      this.clinicSig.set(this.clinic)
    }
    if (changes['facility']) {
      this.facilitySig.set(this.facility)
    }
    if (changes['patient']) {
      this.patientSig.set(this.patient)
    }
    // Picker fetches its own list off [patientId]; setDraftPatient stamps the unsaved draft so saves carry it.
    const patientId = this.patient?.id || this.appointment?.patientId || null
    if ((changes['patient'] || changes['appointment']) && patientId) {
      this.flow.setDraftPatient(patientId)
    }
  }

  get isNewEstimate(): boolean {
    return !this.estimateId || this.estimateId === 'new'
  }

  showTransactionsCard(balance: EstimateBalance | null): boolean {
    if (!balance) {
      return false
    }
    if (isConfirmedWorkflow(balance.workflowStatus)) {
      return true
    }
    if ((balance.paidAmount ?? 0) > 0) {
      return true
    }
    if ((balance.refundedAmount ?? 0) > 0) {
      return true
    }
    return false
  }

  showCollectPaymentCard(balance: EstimateBalance | null): boolean {
    if (!balance) {
      return false
    }

    if (isConfirmedWorkflow(balance.workflowStatus)) {
      return true
    }

    return false
  }

  shouldDelegateToModifySession(vm: EstimateBalanceViewModel): boolean {
    if (!vm.balance) {
      return false
    }

    return vm.balance.workflowStatus === EstimateBalanceWorkflowStatus.CONFIRMED || vm.estimate?.modifySnapshot != null
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  retry(): void {
    this.hydrate()
  }

  onFinalize(): void {
    const estimateId = this.state.estimate()?.id
    if (estimateId == null) {
      return
    }
    // Modify-path finalize: hydrate after to clear local modifySnapshot (backend cleared it,
    // but finalize() returns only the balance). Non-modify finalize has no snapshot, and a
    // hydrate would clobber fresher local state with a stale GET.
    const wasModifying = this.state.modifying()
    const op$ = wasModifying
      ? this.flow.finalize().pipe(
        concatMap(() => this.flow.hydrate(estimateId).pipe(catchError(() => of(null)))),
      )
      : this.flow.finalize()
    this.actions.run(op$)
  }

  onStartModify(): void {
    const estimateId = this.state.estimate()?.id
    if (!estimateId) {
      return
    }
    this.actions.run(
      this.flow.startModify().pipe(
        catchError((err: unknown) => {
          // 400 race — another operator opened the session first. Re-hydrate so we land
          // inside their in-flight session instead of the stale finalized view. Match on raw message, not the i18n key.
          const raw = this.actions.extractRawMessage(err)
          if (/active modify session/i.test(raw)) {
            return this.flow.hydrate(estimateId).pipe(map(() => null))
          }
          throw err
        }),
      ),
      { onErrorKey: 'balance.modify-session.start-error' },
    )
  }

  onDiscardModify(): void {
    if (!this.state.estimate()?.id) {
      return
    }
    this.actions.run(this.flow.discardModify(), {
      onErrorKey: 'balance.modify-session.discard-error',
    })
  }

  isDirty(): boolean {
    return this.state.modifying() || this.state.dirty()
  }

  onRevert(): void {
    if (this.state.estimate()?.id == null) {
      return
    }
    this.actions.run(this.flow.revert())
  }

  onViewHistory(): void {
    const estimateId = this.state.estimate()?.id
    if (!estimateId) {
      return
    }
    const data: BalanceHistoryDialogData = { estimateId }
    this.dialog.open(BalanceHistoryDialogComponent, {
      panelClass: 'dialog-form',
      data,
    })
  }

  onChargeBreakdown(data: BalanceChargeBreakdownDialogData): void {
    this.dialog.open(BalanceChargeBreakdownDialogComponent, {
      data,
      width: '52rem',
      maxWidth: 'calc(100vw - 2rem)',
      maxHeight: 'calc(100vh - 2rem)',
      autoFocus: '[data-testid="visit-row-breakdown-search"]',
    })
  }

  onPbTrace(grouping: ChargeGrouping): void {
    this.openPricerTrace('pb', grouping)
  }

  onHbTrace(grouping: ChargeGrouping): void {
    this.openPricerTrace('hb', grouping)
  }

  private openPersistedMcsTrace(): void {
    const estimate = this.state.estimate()
    if (!estimate?.id) {
      return
    }

    const apiService = this.injector.get(ApiService)
    apiService.getMCSTraces(estimate.id).subscribe({
      next: (response) => {
        if (!response.traces || response.traces.length === 0) {
          openTraceMessageDialog(
            this.confirmService,
            this.translate.instant('balance.mcs-trace.empty'),
            this.destroy$,
          )
          return
        }

        this.displayMCSTrace(response.traces[0])
      },
      error: (err) => {
        openTraceMessageDialog(
          this.confirmService,
          err?.error?.message ?? this.translate.instant('balance.mcs-trace.load-error'),
          this.destroy$,
        )
      },
    })
  }

  onMcsResponse(): void {
    const trace = this.localMcsTrace()
    if (!trace) {
      this.openPersistedMcsTrace()
      return
    }

    this.displayMCSTrace(trace)
  }

  private displayMCSTrace(trace: NonNullable<ModelClaimSearchResult['trace']>): void {
    const traceData = {
      id: trace.id,
      query: trace.query,
      serviceType: trace.serviceType,
      facilityId: trace.facilityId,
      correlationId: trace.correlationId,
      timestamp: trace.timestamp,
      durationMs: trace.durationMs,
      request: trace.requestBody,
      response: trace.rawResponse,
    }

    openJsonTraceDialog(
      this.confirmService,
      this.translate.instant('balance.mcs-trace.title'),
      traceData,
      '',
      this.destroy$,
    )
  }

  private openPricerTrace(kind: 'pb' | 'hb', grouping: ChargeGrouping): void {
    const estimate = this.state.estimate()
    if (!estimate) {
      return
    }

    const response = kind === 'pb'
      ? findPricerTraceEntry(estimate.pbResponses, grouping)
      : findPricerTraceEntry(estimate.hbResponses, grouping)
    const request = kind === 'pb'
      ? findPricerTraceEntry(estimate.pbRequests, grouping)
      : findPricerTraceEntry(estimate.hbRequests, grouping)

    const t = (key: string, params?: object): string =>
      this.translate.instant(`patient-details.estimate-card.advanced.${key}`, params)

    const message = buildPricerTraceMessage(kind, response, request, t)
    if (!message) {
      return
    }

    openPricerTraceDialog(this.confirmService, message, this.destroy$)
  }

  onPaymentPlanCreated(updated: EstimateBalance): void {
    this.applyBalanceUpdateAfterPlanChange(updated)
  }

  onPaymentPlanCanceled(updated: EstimateBalance): void {
    this.applyBalanceUpdateAfterPlanChange(updated)
  }

  /**
   * Fans a refresh across the three inline children after a plan create/cancel:
   * - transactions card — Stripe's first webhook charge can land between the create response and next render
   * - plan + collect-payment cards — pick up any new card the operator added mid-flow
   * Refreshes are no-ops when the ViewChild is undefined.
   */
  private applyBalanceUpdateAfterPlanChange(updated: EstimateBalance): void {
    this.state.setBalance(updated)
    this.transactionsCard?.refresh()
    this.collectPaymentCard?.refresh()
  }

  /** Replays (applied=false) are no-ops; real applications swap state + fan refresh to the three children. */
  onPaymentApplied(result: { balance: EstimateBalance, applied: boolean, reason?: 'replay' }): void {
    if (!result.applied) {
      return
    }
    this.state.setBalance(result.balance)
    this.transactionsCard?.refresh()
    this.collectPaymentCard?.refresh()
  }

  onRefundRequested(transaction: TransactionModel): void {
    const balance = this.state.balance()
    if (!balance) {
      return
    }
    const data: BalanceRefundDialogData = { balance, transaction }
    const ref = this.dialog.open<BalanceRefundDialogComponent, BalanceRefundDialogData, BalanceRefundDialogResult>(
      BalanceRefundDialogComponent,
      { panelClass: 'dialog-form', data },
    )
    ref.afterClosed().pipe(take(1), takeUntil(this.destroy$)).subscribe((result) => {
      if (!result || result.applied === false) {
        return
      }
      // Refund route returns only the Transaction; refetch the balance for the new paid/refunded
      // totals. Through actions.run so a failed refetch snackbars — the refund already succeeded,
      // and silent failure would strand the operator on stale numbers.
      const refresh$ = this.api.getBalanceById(balance.id).pipe(tap((updated) => {
        if (updated) {
          this.state.setBalance(updated)
        }
        this.transactionsCard?.refresh()
      }))
      this.actions.run(refresh$, { onErrorKey: 'balance.refund.refresh-error' })
    })
  }

  onSave(): void {
    if (this.state.estimate() == null) {
      return
    }
    const wasNew = this.isNewEstimate
    this.actions.run(
      this.flow.save().pipe(tap((saved: EstimateData) => {
        if (wasNew) {
          this.navigateToSavedEstimate(saved)
        }
      })),
    )
  }

  onDelete(): void {
    const estimate = this.state.estimate()
    if (estimate?.id == null) {
      return
    }
    const patientId = estimate.patientId || this.patient?.id
    this.confirmService.confirm('estimate.confirm-delete-estimate')
      .pipe(take(1), filter(confirmed => confirmed), takeUntil(this.destroy$))
      .subscribe(() => {
        this.actions.run(this.flow.delete().pipe(tap(() => {
          this.estimateDeleted.emit()
          if (!this.embedded && patientId) {
            this.router.navigate(['/patient', patientId])
          }
        })))
      })
  }

  onAppointmentSelectionChange(ids: string[]): void {
    // Under unscheduled, the picker already emitted (unscheduledChange); the empty
    // (selectionChange) that follows is just it clearing its scheduled model — don't sync.
    if (this.state.unscheduled()) {
      return
    }
    // Local only — appointment edits are not persisted on dropdown toggle. saveAndCalculate
    // commits a CoC appointment change to the backend when the user clicks Calculate, so the
    // estimate is never half-updated and a transient request failure is cleanly retryable.
    this.flow.updateAppointmentSelection(ids)
  }

  onCocModeChange(on: boolean): void {
    this.state.setCocMode(on)
    if (!on) {
      // Leaving multi-mode also leaves unscheduled — single-select doesn't offer that row.
      this.flow.setUnscheduledMode(false)
      // Clear (rather than collapse) — collapsing would have to guess which CoC pick to keep.
      // Routed through onAppointmentSelectionChange so the backend sync fires on persisted estimates.
      if (this.state.selectedAppointmentIds().length > 1) {
        this.onAppointmentSelectionChange([])
      }
    }
  }

  onUnscheduledChange(on: boolean): void {
    this.flow.setUnscheduledMode(on)
  }

  onCalculate(): void {
    // Save+calculate when: modifying (no separate Save in the modify bar), new draft (no id
    // yet — calculate() alone would 4xx), or dirty (plain calculate would recompute against
    // stale server charges). Else calculate alone is enough — skip the redundant PATCH.
    const wasNew = !this.state.estimate()?.id
    const needsSave = this.state.modifying() || wasNew || this.state.dirty()
    const base$ = needsSave ? this.flow.saveAndCalculate() : this.flow.calculate()
    // When Calculate is what creates the estimate, swap the URL from `new` to `:id` (mirror onSave).
    const op$ = wasNew
      ? base$.pipe(tap(() => {
        const saved = this.state.estimate()
        if (saved) {
          this.navigateToSavedEstimate(saved)
        }
      }))
      : base$
    this.actions.run(op$, {
      onProgressKey: 'balance.charges.calculate-in-progress',
      onSuccessKey: 'balance.charges.calculate-success',
      onErrorKey: 'balance.charges.calculate-error',
    })
  }

  onChargesReset(type: BalanceChargesResetType): void {
    if (type === 'clear') {
      this.flow.clearCharges()
    } else if (type === 'template') {
      const templateId = this.state.estimate()?.templateId
      if (templateId) {
        this.runLoadTemplate(templateId)
      }
    } else if (type === 'estimate' && this.state.estimate()?.id) {
      this.hydrate()
    }
  }

  onTemplateSelected(templateId: string): void {
    this.runLoadTemplate(templateId)
  }

  // actions.run so a failed fetch snackbars — a bare .subscribe() would silently drop errors.
  private runLoadTemplate(templateId: string): void {
    this.actions.run(
      this.flow.loadTemplate(templateId),
      { onErrorKey: 'balance.charges.load-template-error' },
    )
  }

  // A claim REPLACES the charge set, doesn't append (matches legacy semantics).
  // Keep the picked charges local until Save/Calculate persists them. Rehydrating here would
  // clobber dirty charge edits with the server's pre-search estimate snapshot.
  onClaimAdded(claim: ModelClaimSearchResult | null): void {
    if (!claim) {
      return
    }
    // A no-results search carries a trace worth inspecting, so surface it while leaving existing charges intact.
    if ((claim.charges?.length ?? 0) > 0) {
      this.flow.replaceCharges(claim.charges)
    }
    this.localMcsTrace.set(claim.trace ?? null)
    const estimate = this.state.estimate()
    if (claim.trace && estimate) {
      this.state.setEstimate({
        ...estimate,
        modelClaimSearchTraces: [claim.trace],
      })
    }
  }

  onAddDiscount(payload: { index: number; type: EstimateChargeDiscountType }): void {
    this.formService.addDiscountControls(this.estimateForm, payload.index, payload.type)
    this.state.markDirty()
  }

  onRemoveDiscount(payload: { index: number }): void {
    this.formService.removeDiscountControls(this.estimateForm, payload.index)
    this.state.markDirty()
  }

  onChargesChanged(): void {
    const estimate = this.state.estimate()
    if (estimate) {
      const charges = this.formService.getCharges(this.estimateForm)
      this.state.setEstimate({ ...estimate, charges })
      this.state.markDirty()
    }
  }

  // Persist the pick + reload benefits + mark dirty so the next calculate reprices.
  onPrimaryInsuranceChange(insurance: PatientInsurance): void {
    this.actions.run(
      this.flow.selectPrimaryInsurance(insurance).pipe(tap(() => this.state.markDirty())),
      { onErrorKey: 'balance.benefits.select-error' },
    )
  }

  onSecondaryInsuranceChange(insurance: PatientInsurance): void {
    this.actions.run(
      this.flow.selectSecondaryInsurance(insurance).pipe(tap(() => this.state.markDirty())),
      { onErrorKey: 'balance.benefits.select-error' },
    )
  }

  onBenefitsChanged(): void {
    const estimate = this.state.estimate()
    if (!estimate) {
      return
    }
    const inputs = this.formService.benefitsFormToInputs(this.benefitsForm)
    this.state.setEstimate({ ...estimate, inputs })
    this.state.markDirty()
  }

  onPrintLetter(): void {
    this.actions.run(this.flow.printLetter(), {
      onSuccessKey: 'balance.summary.print-success',
      onErrorKey: 'balance.summary.print-error',
    })
  }

  onSendLetter(channel: BalanceSendChannel): void {
    this.actions.run(this.flow.sendLetter(channel, 'link', this.patient), {
      onSuccessKey: 'balance.summary.send-success',
      onErrorKey: 'balance.summary.send-error',
    })
  }

  // PDF is email-only, matching the legacy estimator's send-document action.
  onSendDocument(): void {
    this.actions.run(this.flow.sendLetter('email', 'pdf', this.patient), {
      onSuccessKey: 'balance.summary.send-success',
      onErrorKey: 'balance.summary.send-error',
    })
  }

  private hydrate(): void {
    this.localMcsTrace.set(null)
    // Errors are handled by flow → state.markError → 'error' loading state in the template.
    // Empty handler just silences the unhandled-rejection log. Insurance re-resolution rides
    // state.estimateChanged$ (in EstimateInsuranceLoader); no explicit call here.
    this.flow.hydrate(this.estimateId, this.buildDraftSeed())
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: () => undefined })
  }

  private navigateToSavedEstimate(saved: EstimateData): void {
    this.estimateSaved.emit(saved)
    if (this.embedded) {
      return
    }
    const patientId = saved.patientId || this.patient?.id
    if (!patientId || !saved.id) {
      return
    }
    this.router.navigate(['/patient', patientId, 'estimates', saved.id], {
      queryParamsHandling: 'preserve',
    })
  }

  private buildDraftSeed(): Partial<EstimateData> | undefined {
    if (!this.isNewEstimate) {
      return undefined
    }
    // Carry the patient through the draft even without an appointment — otherwise the picker's
    // [patientId] briefly dips to '' between reset and setDraftPatient, triggering
    // picker.ngOnChanges → setAppointments([]), which wipes the cache resetForNewEstimate preserves.
    const patientId = this.appointment?.patientId ?? this.patient?.id ?? null
    const facilityId = this.appointment?.locationId ?? this.facility?.id ?? null
    const appointmentIds = this.appointment?.id ? [this.appointment.id] : []
    if (!patientId && !facilityId && appointmentIds.length === 0) {
      return undefined
    }
    const seed: Partial<EstimateData> = { appointmentIds }
    if (patientId) {
      seed.patientId = patientId
    }
    if (facilityId) {
      seed.facilityId = facilityId
    }
    return seed
  }
}
